import React, { useEffect, useState, useCallback } from "react";
import "../../Styles/Admin/ReportTable.css";
import { API_BASE_URL } from "../../apiConfig";
import { Download, AlertCircle, Loader, FileText } from "lucide-react";
import Pagination from "../Common/Pagination";

const ReportTable = ({ filters }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const rowsPerPage = 10;

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * rowsPerPage;

      const query = new URLSearchParams({
        search: filters.search || "",
        startDate: filters.startDate || "",
        endDate: filters.endDate || "",
        limit: rowsPerPage,
        offset: offset,
      });

      const response = await fetch(`${API_BASE_URL}/admin/continuous-attendance-report?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
        setTotalCount(data.total || 0);
      } else {
        setError(data.message || "Failed to fetch report data");
        console.error("Failed to fetch report data:", data.message);
      }
    } catch (err) {
      setError("An error occurred while fetching data");
      console.error("Error fetching report data:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, rowsPerPage]);

  const handleDownloadAll = async () => {
    const { startDate, endDate, search } = filters;
    const downloadUrl = `${API_BASE_URL}/admin/generate-excel?startDate=${startDate}&endDate=${endDate}&search=${search || ""}`;

    try {
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "attendance_report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      setCurrentPage(1); // Reset to first page on new filters
    }
  }, [filters]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchReportData();
    }
  }, [filters, currentPage, fetchReportData]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "present":
        return "rt-status-present";
      case "absent":
        return "rt-status-absent";
      case "late":
        return "rt-status-late";
      default:
        return "rt-status-default";
    }
  };

  return (
    <div className="rt-container">
      {loading ? (
        <div className="rt-loading">
          <Loader size={40} className="rt-spinner" />
          <p>Loading report data...</p>
        </div>
      ) : error ? (
        <div className="rt-error">
          <AlertCircle size={40} color="#3b82f6" />
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="rt-retry-btn" onClick={fetchReportData}>
            Try Again
          </button>
        </div>
      ) : reportData.length === 0 ? (
        <div className="rt-empty">
          <FileText size={40} color="#6b7280" />
          <h3>No Report Data</h3>
          <p>No report data found for the selected criteria.</p>
        </div>
      ) : (
        <>
          <div className="rt-header">
            <h3>Attendance Report</h3>
            <button
              className="rt-download-all-btn"
              onClick={handleDownloadAll}
              title="Download Complete Report"
            >
              <Download size={16} />
              <span>Download Excel Report</span>
            </button>
          </div>

          <div className="rt-table-wrapper">
            <table className="rt-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Date</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((record, index) => (
                  <tr key={index} className="rt-table-row">
                    <td className="rt-user-cell">
                      <div className="rt-user-avatar">
                        {record.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div className="rt-user-info">
                        <div className="rt-user-name">{record.name}</div>
                        <div className="rt-user-email">{record.email}</div>
                      </div>
                    </td>
                    <td>{record.date}</td>
                    <td>{record.checkInTime || "N/A"}</td>
                    <td>{record.checkOutTime || "N/A"}</td>
                    <td>
                      <span className={`rt-status-badge ${getStatusClass(record.status)}`}>
                        {record.status || "Unknown"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ReportTable;
